import React from "react";
import {
  FiFileText,
  FiEye,
  FiAward,
  FiCheckCircle,
  FiTrendingUp,
  FiBarChart2,
  FiShield,
  FiDownload,
} from "react-icons/fi";

const ReportSheetCard: React.FC = () => {
  const highlights = [
    {
      icon: <FiBarChart2 className="text-indigo-400" size={20} />,
      title: "Automated Grade & Assessment Breakdown",
      description:
        "Seamless computation of Continuous Assessments (CA1, CA2), exams, total scores, subject positions, and class averages.",
    },
    {
      icon: <FiTrendingUp className="text-purple-400" size={20} />,
      title: "Affective & Psychomotor Evaluation",
      description:
        "Comprehensive tracking of student behavioral traits, physical skills, attendance records, and active participation.",
    },
    {
      icon: <FiAward className="text-pink-400" size={20} />,
      title: "Teacher & Principal Endorsements",
      description:
        "Structured remark portals for class teachers and school administrators to provide tailored academic guidance.",
    },
    {
      icon: <FiEye className="text-emerald-400" size={20} />,
      title: "Instant Parent Result Portal",
      description:
        "Parents enjoy instant, transparent access to view and download official termly report cards for their wards anytime, anywhere.",
    },
  ];

  return (
    <div className="mt-16 max-w-5xl mx-auto" data-aos="fade-up">
      <div className="relative rounded-[2.5rem] bg-slate-900 border border-slate-800 text-white p-8 sm:p-12 shadow-2xl overflow-hidden group">
        
        {/* Background Decorative Glow */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none group-hover:bg-indigo-600/30 transition-all duration-700" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl pointer-events-none group-hover:bg-purple-600/30 transition-all duration-700" />

        <div className="relative z-10 space-y-8">
          
          {/* Top Header & Badges */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-slate-800/80">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 px-3.5 py-1.5 rounded-full text-indigo-300 text-xs font-bold tracking-wide uppercase">
                <FiFileText size={14} />
                <span>Automated Academic Reporting</span>
              </div>
              <h3 className="text-2xl sm:text-4xl font-black text-white tracking-tight">
                Termly Electronic Report Sheets
              </h3>
              <p className="text-sm sm:text-base text-slate-300 max-w-2xl font-medium leading-relaxed">
                Generate error-free, beautifully formatted digital report sheets at Mid-Term, End-of-Term, and Annual Cumulative sessions with 1-click precision.
              </p>
            </div>

            {/* Term Badges */}
            <div className="flex flex-wrap md:flex-col gap-2 shrink-0">
              <span className="px-4 py-2 rounded-xl bg-slate-800/90 border border-slate-700/60 text-xs font-bold text-indigo-300 flex items-center gap-2 shadow-sm">
                <FiCheckCircle className="text-emerald-400" size={14} /> Mid-Term Results
              </span>
              <span className="px-4 py-2 rounded-xl bg-slate-800/90 border border-slate-700/60 text-xs font-bold text-purple-300 flex items-center gap-2 shadow-sm">
                <FiCheckCircle className="text-emerald-400" size={14} /> End-of-Term Reports
              </span>
              <span className="px-4 py-2 rounded-xl bg-slate-800/90 border border-slate-700/60 text-xs font-bold text-pink-300 flex items-center gap-2 shadow-sm">
                <FiCheckCircle className="text-emerald-400" size={14} /> Cumulative Final Sheets
              </span>
            </div>
          </div>

          {/* Core Feature Highlights Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
            {highlights.map((item, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-slate-800/50 border border-slate-800 hover:border-slate-700/80 transition-all duration-300 space-y-3 hover:bg-slate-800/80"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-700/60 flex items-center justify-center">
                  {item.icon}
                </div>
                <h4 className="text-base font-bold text-white tracking-wide">
                  {item.title}
                </h4>
                <p className="text-xs sm:text-sm text-slate-400 font-medium leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Footer Access Note */}
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-800/80 text-xs font-semibold text-slate-400">
            <div className="flex items-center gap-2">
              <FiShield className="text-emerald-400 text-sm shrink-0" />
              <span>Encrypted & secure academic record storage with role-based access control.</span>
            </div>
            <div className="flex items-center gap-2 bg-indigo-950/80 border border-indigo-800/60 text-indigo-200 px-4 py-2 rounded-xl text-xs font-bold">
              <FiDownload size={14} className="text-indigo-400" />
              <span>Instant PDF Generation & Digital Downloads</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ReportSheetCard;
